class ProductLoader extends HTMLElement {
  constructor() {
    super();
    this.nextUrl = this.getAttribute('data-next-url');
    this.viewportWidth = window.innerWidth;
    this.template = this.getAttribute('data-template');
    if (this.viewportWidth < 750 && this.template === 'collection') {
      this.containerId = this.getAttribute('data-container-id') + '-mobile';
    } else if (this.viewportWidth > 749 && this.template === 'collection') {
      this.containerId = this.getAttribute('data-container-id') + '-desktop';
    } else {
      this.containerId = this.getAttribute('data-container-id');
    }
    this.loaderType = this.getAttribute('data-loader-type');

    // State management
    this._isLoading = false; // Tracks active network fetch (append path)
    this._isPreloading = false; // Tracks background preload fetch
    this._isIntersecting = false; // Tracks if loader is currently intersecting
    this._preloadedContent = null; // Stores preloaded content
    this._preloadedNextUrl = null; // Next URL from preloaded content
    this._preloadedCount = null; // Count from preloaded content

    if (!this.containerId) {
      console.error('ProductLoader requires a data-container-id attribute');
      return;
    }
    if (!this.nextUrl) {
      console.warn(
        'ProductLoader: No next URL provided, pagination will be disabled',
      );
      this.classList.add('hidden');
      this.dataset.nextUrl = ''; // Clear next URL to prevent further loading
      return;
    }
    // Determine the loader type from the data attribute
    // It can be 'ajax-loader' or 'infinite-loader'
    if (!this.loaderType) {
      console.error(
        'ProductLoader requires a data-loader-type attribute with value "ajax-loader" or "infinite-loader"',
      );
      return;
    }
    this.observer = null; // To hold the Intersection Observer instance
  }

  connectedCallback() {
    this.bindEvents();
    if (this.loaderType === 'infinite-loader') {
      this.initializeIntersectionObserver();
    }
  }

  bindEvents() {
    if (this.loaderType === 'ajax-loader') {
      this.addEventListener('click', this.fetchAndAppendProducts.bind(this));
    }
  }
  initializeIntersectionObserver() {
    // Set a much larger margin to preload content well before user reaches the bottom
    // 3000px means we'll start loading content when user is still 3000px away from the loader
    const options = {
      root: null, // Observes within the viewport
      rootMargin: '3000px 0px 3000px 0px', // Large margin for early detection
      threshold: 0, // Fires as soon as even 1px is visible
    };

    this.observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      this._isIntersecting = !!entry?.isIntersecting;
      this.handleIntersection(entries);
    }, options);

    // Observe the loader element itself
    this.observer.observe(this);

    // Start preloading the first batch immediately after page loads
    // Small timeout to let the page finish initial rendering first
    setTimeout(() => {
      this.preloadNextBatch();
    }, 500);
  }

  // New method to preload content in background
  preloadNextBatch() {
    if (this.nextUrl && !this._isPreloading && !this._preloadedContent) {
      this._preloadContent();
    }
  }
  async handleIntersection(entries) {
    const [entry] = entries;

    if (!entry) return;

    // Only append when intersecting
    if (entry.isIntersecting) {
      // If there is preloaded content, append it now
      if (this._preloadedContent) {
        this.appendPreloadedContent();
        return;
      }

      // If not preloaded and not already fetching, fetch and append
      if (!this._isLoading && !this._isPreloading && this.nextUrl) {
        this._isLoading = true;
        try {
          await this.fetchAndAppendProducts();
        } finally {
          this._isLoading = false;
          // After appending, start a background preload of the next page
          setTimeout(() => this.preloadNextBatch(), 200);
        }
      }
    }
  }

  // New method to preload content in the background
  async _preloadContent() {
    if (!this.nextUrl || this._isPreloading || this._preloadedContent) return;

    // Background preload: use separate flag and no visual loader
    this._isPreloading = true;

    try {
      // Fetch the next page without showing any visual loading
      const response = await fetch(this.nextUrl);
      if (!response.ok) {
        throw new Error('Failed to preload the next page of products');
      }

      const nextPage = await response.text();
      const parser = new DOMParser();
      const nextPageDocument = parser.parseFromString(nextPage, 'text/html');

      // Store preloaded content
      const nextPageProducts = nextPageDocument.querySelector(
        `#${this.containerId}`,
      );
      if (nextPageProducts) {
        // Store preloaded HTML content
        this._preloadedContent = nextPageProducts.innerHTML;

        // Store next URL for future preloading
        const nextLoader = nextPageDocument.querySelector('product-loader');
        // @ts-ignore
        this._preloadedNextUrl = nextLoader?.dataset?.nextUrl || null;

        // Store count information
        this._preloadedCount =
          nextPageDocument.querySelector('count-on-grid')?.innerHTML || '';

        // No visual changes yet - content is cached and ready to append instantly when needed
      }
    } catch (error) {
      console.error('Error preloading products:', error);
    } finally {
      this._isPreloading = false;
      // If the loader is already intersecting when preload finishes, append now
      if (this._isIntersecting && this._preloadedContent) {
        this.appendPreloadedContent();
      }
    }
  }

  // Method to append the preloaded content immediately when needed
  appendPreloadedContent() {
    if (!this._preloadedContent) return;

    // Fast append without showing loader - instant content
    const container = document.querySelector(`#${this.containerId}`);
    if (container) {
      // Append without showing loader
      container.insertAdjacentHTML('beforeend', this._preloadedContent);

      // Dispatch event with count information if available
      if (this._preloadedCount) {
        const event = new CustomEvent('update:infinite:product-grid', {
          bubbles: true,
          composed: true,
          detail: {count: this._preloadedCount},
        });
        document.dispatchEvent(event);
      }

      // Update next URL for future pagination
      this.nextUrl = this._preloadedNextUrl;

      // Clear preloaded content as it's now displayed
      this._preloadedContent = null;
      this._preloadedCount = null;

      // Hide loader if we've reached the end
      if (!this._preloadedNextUrl) {
        if (this.observer) this.observer.disconnect();
        this.classList.add('hidden');
        this.dataset.nextUrl = ''; // Clear next URL to prevent further loading
      }
      // Otherwise start preloading the next batch
      else {
        // Trigger next preload after a small delay to prevent overloading
        setTimeout(() => this.preloadNextBatch(), 300);
      }
    }
  }
  async fetchAndAppendProducts() {
    // If we have preloaded content, use it for immediate display
    if (this._preloadedContent) {
      // Don't even show a loader - just append the content we already have
      this.appendPreloadedContent();
      return;
    }

    // Traditional loading path if no preloaded content available
    if (!this.nextUrl) {
      if (this.observer) this.observer.disconnect();
      this.classList.add('hidden');
      this.dataset.nextUrl = ''; // Clear next URL to prevent further loading
      return;
    }

    let contentFetched = false;
    let nextUrl = null;
    let productsHTML = '';
    let countHTML = '';

    // Only show loader briefly if we don't have preloaded content
    // This should be rare with our preloading strategy
    this.classList.add('loading');

    try {
      // Fetch the next page immediately
      const response = await fetch(this.nextUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch the next page of products');
      }

      // Get the HTML content
      const nextPage = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const nextPageDocument = parser.parseFromString(nextPage, 'text/html');

      // Find products container
      const nextPageProducts = nextPageDocument.querySelector(
        `#${this.containerId}`,
      );

      if (nextPageProducts) {
        contentFetched = true;

        // Get next URL from loader component
        const nextLoader = nextPageDocument.querySelector('product-loader');
        // @ts-ignore
        nextUrl = nextLoader?.dataset?.nextUrl || null;

        // Get the HTML content for products
        productsHTML = nextPageProducts.innerHTML;

        // Get count information if available
        countHTML =
          nextPageDocument.querySelector('count-on-grid')?.innerHTML || '';
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      // Always remove loading state BEFORE manipulating DOM
      this.classList.remove('loading');

      // Only update DOM if we successfully fetched content
      if (contentFetched) {
        // Update nextUrl for future pagination
        this.nextUrl = nextUrl;

        // Insert new content
        const container = document.querySelector(`#${this.containerId}`);
        if (container && productsHTML) {
          container.insertAdjacentHTML('beforeend', productsHTML);
        }

        // Dispatch event if we have count data
        if (countHTML) {
          const event = new CustomEvent('update:infinite:product-grid', {
            bubbles: true,
            composed: true,
            detail: {count: countHTML},
          });
          document.dispatchEvent(event);
        }

        // Hide loader if we've reached the end
        if (!nextUrl) {
          if (this.observer) this.observer.disconnect();
          this.classList.add('hidden');
          this.dataset.nextUrl = ''; // Clear next URL to prevent further loading
        }
        // Otherwise, immediately start preloading the next batch
        else {
          // Queue up preloading for the next batch after a short delay
          setTimeout(() => this.preloadNextBatch(), 300);
        }
      }
    }
  }
}
customElements.define('product-loader', ProductLoader);
